/**
 * React Query hooks for the investor portal email log.
 */

import { keepPreviousData } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { portalQueryKeys } from '@/portal/shared/hooks/queryKeys';
import type {
  InvestorEmailLogDetail,
  InvestorEmailLogList,
  InvestorEmailLogParams,
} from '@/types/investorEmail';
import { EmailsService } from '../services/emails-service';

/** Fetch one page of the investor's email history. */
export const useEmails = (params: InvestorEmailLogParams = {}) => {
  return useApiQuery<InvestorEmailLogList>(
    portalQueryKeys.emails.list(params),
    () => EmailsService.getAllEmails(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      placeholderData: keepPreviousData,
    }
  );
};

/** Fetch a single email (with rendered body); idle until an id is provided. */
export const useEmailDetail = (emailId: string | undefined) => {
  return useApiQuery<InvestorEmailLogDetail>(
    portalQueryKeys.emails.detail(emailId),
    () => EmailsService.getEmailDetail(emailId as string),
    {
      enabled: !!emailId,
      staleTime: 5 * 60 * 1000,
    }
  );
};
